import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChefHat, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const _invited = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("invite") : "") || "";
  const [mode, setMode] = useState(_invited ? "register" : "login");
  const [email, setEmail] = useState(_invited);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: email.trim() });
        toast.success("Si un compte existe, un email de réinitialisation a été envoyé.");
        setMode("login");
      } else if (mode === "login") {
        await login(email.trim(), password);
        toast.success("Bienvenue à table !");
      } else {
        await register(email.trim(), password, name.trim());
        toast.success("Bienvenue à table !");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6" style={{ background: "var(--at-bg)" }} data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center at-fade-up">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl text-white at-shadow-lg" style={{ background: "var(--at-brand)" }}>
            <ChefHat size={32} />
          </div>
          <h1 className="font-display text-4xl font-bold">À Table</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--at-text-2)" }}>Vos recettes, vos repas, vos courses — en famille.</p>
        </div>

        <form onSubmit={submit} className="space-y-3 at-fade-up" style={{ animationDelay: "60ms" }}>
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Prénom</Label>
              <div className="relative">
                <User size={17} className="absolute left-3.5 top-3.5" style={{ color: "var(--at-muted)" }} />
                <Input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre prénom" className="h-12 rounded-2xl border-black/10 bg-white pl-10" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Email</Label>
            <div className="relative">
              <Mail size={17} className="absolute left-3.5 top-3.5" style={{ color: "var(--at-muted)" }} />
              <Input data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.fr" className="h-12 rounded-2xl border-black/10 bg-white pl-10" />
            </div>
          </div>
          {mode !== "forgot" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Mot de passe</Label>
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-3.5" style={{ color: "var(--at-muted)" }} />
                <Input data-testid="password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-2xl border-black/10 bg-white pl-10" />
              </div>
            </div>
          )}
          {mode === "login" && (
            <div className="text-right">
              <button type="button" data-testid="forgot-link" onClick={() => setMode("forgot")} className="text-xs font-medium underline" style={{ color: "var(--at-text-2)" }}>
                Mot de passe oublié ?
              </button>
            </div>
          )}
          <Button data-testid="submit-auth-btn" type="submit" disabled={busy}
            className="h-13 w-full rounded-2xl py-3 text-base font-semibold text-white at-shadow-lg at-press" style={{ background: "var(--at-brand)" }}>
            {busy ? "..." : mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}
          </Button>
          {mode === "forgot" && (
            <button type="button" data-testid="back-to-login" onClick={() => setMode("login")} className="w-full text-center text-sm font-medium underline" style={{ color: "var(--at-text-2)" }}>
              Retour à la connexion
            </button>
          )}
        </form>

        {mode !== "forgot" && (
          <>
            <div className="my-4 flex items-center gap-3 at-fade-up" style={{ animationDelay: "100ms" }}>
              <div className="h-px flex-1" style={{ background: "var(--at-border)" }} />
              <span className="text-xs" style={{ color: "var(--at-muted)" }}>ou</span>
              <div className="h-px flex-1" style={{ background: "var(--at-border)" }} />
            </div>

            <Button data-testid="google-login-btn" onClick={loginWithGoogle} variant="outline"
              className="h-13 w-full rounded-2xl border-black/10 bg-white py-3 text-base font-semibold at-press at-fade-up" style={{ animationDelay: "120ms" }}>
              <img src="https://www.google.com/favicon.ico" alt="" className="mr-2 h-5 w-5" /> Continuer avec Google
            </Button>

            <p className="mt-6 text-center text-sm at-fade-up" style={{ color: "var(--at-text-2)", animationDelay: "150ms" }}>
              {mode === "login" ? "Pas encore de compte ?" : "Déjà un compte ?"}{" "}
              <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold underline" style={{ color: "var(--at-brand)" }}>
                {mode === "login" ? "Créer un compte" : "Se connecter"}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
